
import React, { useState } from 'react';
import { Mail, ArrowRight, CheckCircle, Bell, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

// Google Form submission helper function for newsletter
const submitNewsletterToGoogleForm = async (email: string) => {
  // Google Form field IDs from the URL
  const formUrl = "https://docs.google.com/forms/d/e/1FAIpQLSfHtP98026SpH01PkPe3oIvA1Zkfh_iGI4mRp3L8rZ1mNMppA/formResponse";
  
  // Create form data object
  const formData = new FormData();
  formData.append("entry.28031474", email);
  
  // Using a no-CORS approach to submit the form
  return fetch(formUrl, {
    method: "POST",
    mode: "no-cors",
    body: formData
  });
};

const NewsletterSection: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address to subscribe.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit to Google Form
      await submitNewsletterToGoogleForm(email);
      
      console.log('Newsletter subscription submitted for:', email);
      
      toast({
        title: "Subscription Successful",
        description: "Thank you for subscribing to Manamu Media newsletter!",
        duration: 5000,
      });
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Subscription Failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-50/80 via-secondary/50 to-amber-50/60"></div>
      <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-white to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white to-transparent"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2">
            {/* Left column - visually appealing graphic */}
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-10 flex items-center justify-center relative overflow-hidden">
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-white/10 rounded-full"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full"></div>
              
              <div className="text-white relative z-10 space-y-6">
                <div className="inline-flex items-center justify-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-2">
                  <Bell className="h-5 w-5 text-white mr-2" />
                  <span className="text-white font-medium">Stay in the Loop</span>
                </div>
                
                <h3 className="text-2xl md:text-3xl font-bold">Be the first to know</h3>
                
                <p className="text-white/90">
                  Get exclusive insights and analysis from our expert team delivered directly to your inbox.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 mr-2 text-white/90 mt-0.5" />
                    <p className="text-white/90">Breaking news alerts</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 mr-2 text-white/90 mt-0.5" />
                    <p className="text-white/90">Exclusive interviews</p>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 mr-2 text-white/90 mt-0.5" />
                    <p className="text-white/90">Expert analysis and insights</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right column - subscription form */}
            <div className="p-10">
              <div className="space-y-6">
                <div className="inline-flex items-center justify-center">
                  <Mail className="h-6 w-6 text-amber-600 mr-3" />
                  <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Subscribe Today</h2>
                </div>
                
                <p className="text-gray-600">
                  Join thousands of informed readers and get our latest articles, analysis, and insights delivered straight to your inbox.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-gray-700">Email address</label>
                    <div className="relative">
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="h-12 w-full pl-10"
                        aria-label="Email address for newsletter"
                      />
                      <Mail className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 transition-all group h-12"
                  >
                    Subscribe
                    {isSubmitting ? (
                      <span className="ml-2 h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    ) : (
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    )}
                  </Button>
                </form>
                
                <div className="flex items-center text-sm text-gray-500">
                  <Shield className="h-4 w-4 mr-2 text-amber-500" />
                  <p>We respect your privacy and won't share your email.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;
